import { defineStore } from 'pinia'
import { ref } from 'vue'
import axios from '@/utils/apiClient'

export const useMenuStore = defineStore('menu', () => {
    const allowedRoutes = ref<Set<string>>(new Set())
    const allowedPrefixes = ref<Set<string>>(new Set())
    const isLoaded = ref(false)
    let fetchPromise: Promise<void> | null = null

    /**
     * Normalize a route path to lowercase with leading slash.
     */
    function normalizePath(path: string): string {
        let p = path.trim().toLowerCase()
        if (!p.startsWith('/')) p = '/' + p
        // Remove trailing slash (except root)
        if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1)
        return p
    }

    /**
     * Extract all routes (including children) from menu items.
     * - Admin format (uses `to` with `children`): only adds exact leaf routes.
     * - Normal user format (uses `link`, no children): adds exact route + parent prefix
     *   so sub-pages (e.g. /user/leave/history under /user/leave/menu) are also accessible.
     */
    function setMenuItems(menus: any[]) {
        const routes = new Set<string>()
        const prefixes = new Set<string>()

        const extract = (items: any[]) => {
            for (const item of items) {
                const hasChildren = item.children && Array.isArray(item.children) && item.children.length > 0

                if (hasChildren) {
                    // Admin menu with children — only recurse into children
                    extract(item.children)
                    continue
                }

                // Leaf item — add as exact allowed route
                const route = item.to || item.link
                if (!route) continue

                const normalized = normalizePath(route)
                routes.add(normalized)

                // For user menu items (link field), also handle sub-route access
                if (item.link) {
                    // Determine the effective path (some links omit /user/ prefix)
                    let effectivePath = normalized
                    if (!effectivePath.startsWith('/user/') && !effectivePath.startsWith('/frames/')) {
                        effectivePath = '/user/' + effectivePath.slice(1)
                        routes.add(effectivePath)
                    }

                    // Extract parent prefix for sub-route access
                    // e.g. /user/leave/menu → prefix /user/leave → allows /user/leave/*
                    // Only for paths with 3+ meaningful segments to avoid overly broad prefixes
                    const parts = effectivePath.split('/')
                    if (parts.length >= 4) {
                        const prefix = parts.slice(0, -1).join('/')
                        prefixes.add(prefix)
                    }
                }
            }
        }

        extract(menus)
        allowedRoutes.value = routes
        allowedPrefixes.value = prefixes
        isLoaded.value = true
    }

    /**
     * Fetch menu from API and populate allowed routes.
     * Caches the promise so multiple calls don't trigger duplicate requests.
     */
    async function fetchAndSetMenu(): Promise<void> {
        if (isLoaded.value) return
        if (fetchPromise) return fetchPromise

        fetchPromise = (async () => {
            try {
                const userStr = localStorage.getItem('user')
                let isAdmin = false

                if (userStr) {
                    const user = JSON.parse(userStr)
                    const role = (user.role || '').toLowerCase().trim()
                    if (['admin', 'superadmin', 'coo', 'hr department'].some(r => role.includes(r))) {
                        isAdmin = true
                    }
                }

                const res = await axios.get(`Auth/GetAccessMenu?IsAdmin=${isAdmin}`)
                const menus = Array.isArray(res.data) ? res.data : []
                setMenuItems([{ title: 'Dashboard', to: '/dashboard' }, ...menus])
            } catch (err) {
                console.error('Failed to fetch menu for route guard:', err)
                isLoaded.value = true
            } finally {
                fetchPromise = null
            }
        })()

        return fetchPromise
    }

    function isRouteAllowed(path: string): boolean {
        if (!isLoaded.value) return true
        const normalized = normalizePath(path)

        // Exact match
        if (allowedRoutes.value.has(normalized)) return true

        // Prefix match for user sub-routes
        // e.g. /user/leave is a prefix → /user/leave/request-form is allowed
        for (const prefix of allowedPrefixes.value) {
            if (normalized.startsWith(prefix + '/')) return true
        }

        return false
    }

    function clearMenu() {
        allowedRoutes.value = new Set()
        allowedPrefixes.value = new Set()
        isLoaded.value = false
        fetchPromise = null
    }

    return { allowedRoutes, allowedPrefixes, isLoaded, setMenuItems, fetchAndSetMenu, isRouteAllowed, clearMenu }
})
